import { baseApi } from 'core/api';
import { useToastStore } from 'store/modules';

const state = () => ({
    _showAITollBar: false,
    _showAIMessageTollBar: false,
    _loading: false,
    _originalMessage: null,
    _paraphrasedMessage: null,
});

const getters = {
    showAITollBar: (state) => state._showAITollBar,
    showAIMessageTollBar: (state) => state._showAIMessageTollBar,
    messageLoading: (state) => state._loading,
    originalMessage: (state) => state._originalMessage,
    paraphrasedMessage: (state) => state._paraphrasedMessage,
};

const actions = {
    changeAITollBarViewStatus(status) {
        this._setTextingAssistantStateUpdate({
            _showAITollBar: status,
        });
    },

    changeAIMessageTollBarViewStatus(status) {
        this._setTextingAssistantStateUpdate({
            _showAIMessageTollBar: status,
        });
    },

    resetAI() {
        this._setTextingAssistantStateUpdate({
            _showAIMessageTollBar: false,
            _showAITollBar: false,
            _loading: false,
            _originalMessage: null,
            _paraphrasedMessage: null,
        });
    },

    getParaphrasedMessage({ message, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .get('ai/api/messages/autocorrection', {
                params: { message: message },
            })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);
                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);
                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);
                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    getSuggestResponseMessage({ message, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .get('ai/api/messages/suggest-answer', {
                params: { message: message },
            })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);
                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);

                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);

                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    getExpandMessage({ message, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .get('ai/api/messages/expand', {
                params: { message: message },
            })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);
                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);

                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);

                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    getShortenMessage({ message, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .get('ai/api/messages/shorten', {
                params: { message: message },
            })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);
                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);

                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);

                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    getToneImprovementMessage({ message, toneType, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .get('ai/api/messages/tone-improvement', {
                params: {
                    message: message,
                    type: toneType,
                },
            })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);
                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);
                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);

                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    getConversationsSuggestResponseMessage({ messages, isTryAgain = false }) {
        this._setTextingAssistantStateUpdate({
            _loading: true,
        });
        if (!isTryAgain) this.changeAITollBarViewStatus(false);

        return baseApi
            .post('ai/api/conversations/suggest-answer', { messages: messages })
            .then((response) => {
                this._setTextingAssistantMessage(response.data);

                if (!isTryAgain) this.changeAIMessageTollBarViewStatus(true);

                return response.data;
            })
            .catch((error) => {
                if (!isTryAgain) this.changeAITollBarViewStatus(true);

                useToastStore().showToast({
                    type: 'danger',
                    message: error.response.data.message,
                });

                return error;
            })
            .finally(() => {
                this._setTextingAssistantStateUpdate({
                    _loading: false,
                });
            });
    },

    _setTextingAssistantMessage(data) {
        this._originalMessage = data.original_message;
        this._paraphrasedMessage = data.result_message;
    },

    _setTextingAssistantStateUpdate(data) {
        for (let key in data) {
            this[key] = data[key];
        }
    },
};

export default {
    state,
    getters,
    actions,
};
